import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Local YYYY-MM-DD (not UTC) so "today" matches the user's calendar.
export function dayKey(d = new Date()) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const LOG_WINDOW_DAYS = 120

// Per-user habit tracker. Loads active habits + a recent window of
// done-logs, exposes a today toggle, CRUD, and a client-side streak.
export function useHabits() {
  const { user } = useAuth()
  const userId = user?.id || null

  const [habits, setHabits] = useState([])
  // Set of "habitId|YYYY-MM-DD" for days marked done.
  const [doneSet, setDoneSet] = useState(() => new Set())
  const [loading, setLoading] = useState(true)

  const refetch = useCallback(async () => {
    if (!userId) { setHabits([]); setDoneSet(new Set()); setLoading(false); return }
    setLoading(true)
    const since = dayKey(new Date(Date.now() - LOG_WINDOW_DAYS * 86400000))
    const [{ data: h }, { data: logs }] = await Promise.all([
      supabase
        .from('habits')
        .select('id, name, emoji, color, weekdays, sort_order, archived')
        .eq('user_id', userId)
        .eq('archived', false)
        .order('sort_order', { ascending: true }),
      supabase
        .from('habit_logs')
        .select('habit_id, day, done')
        .eq('user_id', userId)
        .gte('day', since),
    ])
    setHabits(h || [])
    const s = new Set()
    for (const l of logs || []) if (l.done) s.add(`${l.habit_id}|${l.day}`)
    setDoneSet(s)
    setLoading(false)
  }, [userId])

  useEffect(() => { refetch() }, [refetch])

  const isDone = useCallback((habitId, day = dayKey()) => doneSet.has(`${habitId}|${day}`), [doneSet])

  const toggle = useCallback(async (habitId, day = dayKey()) => {
    if (!userId) return
    const key = `${habitId}|${day}`
    const currentlyDone = doneSet.has(key)
    // Optimistic flip.
    setDoneSet((prev) => {
      const next = new Set(prev)
      if (currentlyDone) next.delete(key); else next.add(key)
      return next
    })
    if (currentlyDone) {
      await supabase.from('habit_logs').delete().eq('habit_id', habitId).eq('day', day)
    } else {
      await supabase.from('habit_logs').upsert(
        { habit_id: habitId, user_id: userId, day, done: true },
        { onConflict: 'habit_id,day' },
      )
    }
  }, [userId, doneSet])

  const addHabit = useCallback(async ({ name, emoji = null, color = null }) => {
    if (!userId || !name?.trim()) return null
    const maxOrder = habits.reduce((m, h) => Math.max(m, h.sort_order || 0), 0)
    const { data, error } = await supabase
      .from('habits')
      .insert({ user_id: userId, name: name.trim(), emoji, color, sort_order: maxOrder + 1 })
      .select('id')
      .single()
    if (error) return null
    await refetch()
    return data?.id || null
  }, [userId, habits, refetch])

  const updateHabit = useCallback(async (id, patch) => {
    if (!userId) return
    await supabase.from('habits').update(patch).eq('id', id)
    await refetch()
  }, [userId, refetch])

  const archiveHabit = useCallback(async (id) => {
    if (!userId) return
    await supabase.from('habits').update({ archived: true }).eq('id', id)
    await refetch()
  }, [userId, refetch])

  // Consecutive days up to (and including) today marked done.
  const streakFor = useCallback((habitId) => {
    let streak = 0
    const d = new Date()
    // Allow today to be unchecked without breaking the streak shown
    // (streak counts back from the most recent done day).
    if (!doneSet.has(`${habitId}|${dayKey(d)}`)) d.setDate(d.getDate() - 1)
    for (;;) {
      if (doneSet.has(`${habitId}|${dayKey(d)}`)) { streak++; d.setDate(d.getDate() - 1) }
      else break
    }
    return streak
  }, [doneSet])

  const todayProgress = useMemo(() => {
    const total = habits.length
    const done = habits.filter((h) => doneSet.has(`${h.id}|${dayKey()}`)).length
    return { done, total }
  }, [habits, doneSet])

  return { habits, loading, isDone, toggle, addHabit, updateHabit, archiveHabit, streakFor, todayProgress, refetch }
}
